import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useChangerStatutTicket,
  useRepondreTicket,
  useTicket,
  useTickets,
} from '../api/queries';
import type { LigneTicket, PrioriteTicket, StatutTicket } from '../api/types';
import { Bouton } from '../components/Boutons';
import { Carte } from '../components/Carte';
import { Selecteur } from '../components/Champs';
import { MessageErreur } from '../components/MessageErreur';
import { Tableau, type Colonne } from '../components/Tableau';
import { formaterDateHeure, formaterNombre, formaterRelatif } from '../lib/format';

/**
 * File du support.
 *
 * L'ordre de la liste vient du serveur : priorité décroissante, puis ancienneté croissante.
 * C'est l'ordre dans lequel un support DOIT traiter sa file — pas l'ordre d'arrivée, qui
 * enterrerait une panne critique sous des demandes de renseignement.
 *
 * Le détail s'ouvre en panneau latéral plutôt qu'en page : un opérateur traite une file, il ne
 * doit pas la perdre de vue à chaque ticket ouvert.
 */

const OPTIONS_STATUT = [
  { valeur: 'OUVERT', libelle: 'Ouvert' },
  { valeur: 'EN_COURS', libelle: 'En cours' },
  { valeur: 'EN_ATTENTE_CLIENT', libelle: 'En attente du client' },
  { valeur: 'RESOLU', libelle: 'Résolu' },
  { valeur: 'FERME', libelle: 'Fermé' },
];

const OPTIONS_PRIORITE = [
  { valeur: 'CRITIQUE', libelle: 'Critique' },
  { valeur: 'HAUTE', libelle: 'Haute' },
  { valeur: 'NORMALE', libelle: 'Normale' },
  { valeur: 'BASSE', libelle: 'Basse' },
];

const COULEUR_PRIORITE: Record<PrioriteTicket, string> = {
  CRITIQUE: '#D0342C',
  HAUTE: '#ED7D0E',
  NORMALE: '#6B7480',
  BASSE: '#9AA3AE',
};

const COULEUR_STATUT: Record<StatutTicket, string> = {
  OUVERT: '#D0342C',
  EN_COURS: '#2563EB',
  EN_ATTENTE_CLIENT: '#ED7D0E',
  RESOLU: '#16733A',
  FERME: '#6B7480',
};

export function SavPage(): JSX.Element {
  const [parametres, setParametres] = useSearchParams();
  const statut = parametres.get('statut') ?? '';
  const priorite = parametres.get('priorite') ?? '';
  const ouvert = parametres.get('ticket') ?? '';

  const { data, error, isPending } = useTickets({
    ...(statut ? { statut } : {}),
    ...(priorite ? { priorite } : {}),
  });

  const majFiltre = (cle: string, valeur: string): void => {
    const suivants = new URLSearchParams(parametres);
    if (valeur === '') suivants.delete(cle);
    else suivants.set(cle, valeur);
    setParametres(suivants, { replace: true });
  };

  const stats = data?.statistiques;

  const colonnes: Colonne<LigneTicket>[] = [
    {
      cle: 'sujet',
      entete: 'Demande',
      rendu: (t) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            #{t.numero} — {t.sujet}
          </div>
          <div style={{ fontSize: 12, color: '#6B7480' }}>
            {t.exploitation?.nom ?? '—'}
            {t.demandeur ? ` · ${t.demandeur.email}` : ''}
            {t.appareil_nom ? ` · ${t.appareil_nom}` : ''}
          </div>
        </div>
      ),
    },
    {
      cle: 'priorite',
      entete: 'Priorité',
      rendu: (t) => (
        <span style={{ color: COULEUR_PRIORITE[t.priorite], fontWeight: 600 }}>{t.priorite}</span>
      ),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (t) => <span style={{ color: COULEUR_STATUT[t.statut] }}>{t.statut}</span>,
    },
    {
      cle: 'age',
      entete: 'Ouvert',
      rendu: (t) => formaterRelatif(t.created_at),
    },
    {
      cle: 'reponse',
      entete: 'Première réponse',
      // Un ticket sans première réponse est un client qui attend sans savoir s'il a été lu.
      rendu: (t) =>
        t.premiere_reponse_at === null ? (
          <span style={{ color: '#D0342C' }}>en attente</span>
        ) : (
          formaterRelatif(t.premiere_reponse_at)
        ),
    },
    {
      cle: 'messages',
      entete: 'Échanges',
      numerique: true,
      rendu: (t) => formaterNombre(t.nb_messages),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <h1 style={{ margin: 0 }}>Support</h1>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: 12,
        }}
      >
        <Tuile libelle="Ouverts" valeur={formaterNombre(stats?.ouverts ?? 0)} accent="#D0342C" />
        <Tuile libelle="En cours" valeur={formaterNombre(stats?.en_cours ?? 0)} />
        <Tuile libelle="Attente client" valeur={formaterNombre(stats?.en_attente_client ?? 0)} />
        <Tuile
          libelle="Critiques non résolus"
          valeur={formaterNombre(stats?.critiques_non_resolus ?? 0)}
          accent={(stats?.critiques_non_resolus ?? 0) > 0 ? '#D0342C' : undefined}
        />
        <Tuile
          libelle="Délai 1ʳᵉ réponse"
          valeur={stats?.delai_premiere_reponse_h === null || stats?.delai_premiere_reponse_h === undefined
            ? '—'
            : `${stats.delai_premiere_reponse_h} h`}
          aide="Moyenne"
        />
        <Tuile
          libelle="Délai de résolution"
          valeur={stats?.delai_resolution_h === null || stats?.delai_resolution_h === undefined
            ? '—'
            : `${stats.delai_resolution_h} h`}
          aide="Moyenne"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: ouvert ? '1fr 420px' : '1fr', gap: 20 }}>
        <Carte>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Selecteur
              label="Statut"
              optionVide="Tous les statuts"
              options={OPTIONS_STATUT}
              value={statut}
              onChange={(e) => {
                majFiltre('statut', e.currentTarget.value);
              }}
            />
            <Selecteur
              label="Priorité"
              optionVide="Toutes les priorités"
              options={OPTIONS_PRIORITE}
              value={priorite}
              onChange={(e) => {
                majFiltre('priorite', e.currentTarget.value);
              }}
            />
          </div>

          {error ? <MessageErreur erreur={error} /> : null}

          <Tableau
            colonnes={colonnes}
            lignes={data?.items ?? []}
            cleDe={(t) => t.id}
            chargement={isPending}
            titreVide="Aucune demande"
            descriptionVide="La file du support est vide."
            surClic={(t) => {
              majFiltre('ticket', t.id);
            }}
          />
        </Carte>

        {ouvert ? <PanneauTicket id={ouvert} onFermer={() => majFiltre('ticket', '')} /> : null}
      </div>
    </div>
  );
}

/** Panneau latéral : fil de discussion et actions sur un ticket. */
function PanneauTicket({ id, onFermer }: { id: string; onFermer: () => void }): JSX.Element {
  const { data, error, isPending } = useTicket(id);
  const repondre = useRepondreTicket(id);
  const changerStatut = useChangerStatutTicket(id);

  const [message, setMessage] = useState('');
  const [notePrivee, setNotePrivee] = useState(false);

  return (
    <Carte
      titre={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1 }}>{data ? `#${data.numero} — ${data.sujet}` : 'Demande'}</span>
          <Bouton variante="discret" petit onClick={onFermer}>
            Fermer
          </Bouton>
        </span>
      }
    >
      {error ? <MessageErreur erreur={error} /> : null}
      {isPending ? <p style={{ color: '#6B7480' }}>Chargement…</p> : null}

      {data ? (
        <div style={{ display: 'grid', gap: 16 }}>
          <div style={{ fontSize: 13, color: '#6B7480' }}>
            {data.exploitation_nom ?? '—'}
            {data.demandeur.email ? ` · ${data.demandeur.email}` : ''}
            {data.demandeur.telephone ? ` · ${data.demandeur.telephone}` : ''}
            {data.appareil_sn ? ` · ${data.appareil_sn}` : ''}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Selecteur
              label="Statut"
              options={OPTIONS_STATUT}
              value={data.statut}
              onChange={(e) => {
                changerStatut.mutate({ statut: e.currentTarget.value as StatutTicket });
              }}
            />
            <Selecteur
              label="Priorité"
              options={OPTIONS_PRIORITE}
              value={data.priorite}
              onChange={(e) => {
                changerStatut.mutate({
                  statut: data.statut,
                  priorite: e.currentTarget.value as PrioriteTicket,
                });
              }}
            />
          </div>

          {/* Message d'origine, puis le fil. Le premier n'est pas dans `messages` : il vient du
              formulaire de l'application, pas d'un échange. */}
          <div
            style={{
              background: '#F6F7F9',
              borderRadius: 10,
              padding: 12,
              fontSize: 14,
              whiteSpace: 'pre-wrap',
            }}
          >
            {data.message ?? '—'}
            <div style={{ fontSize: 11, color: '#9AA3AE', marginTop: 6 }}>
              {formaterDateHeure(data.created_at)}
            </div>
          </div>

          <div style={{ display: 'grid', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
            {data.messages.map((m) => (
              <div
                key={m.id}
                style={{
                  background: m.note_privee ? '#FDF4E3' : m.interne ? '#EEF1FD' : '#F6F7F9',
                  borderLeft: `3px solid ${m.note_privee ? '#ED7D0E' : m.interne ? '#2563EB' : '#E3E7EC'}`,
                  borderRadius: 8,
                  padding: 10,
                  fontSize: 14,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {m.note_privee ? (
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#ED7D0E', marginBottom: 4 }}>
                    NOTE PRIVÉE — non visible du client
                  </div>
                ) : null}
                {m.message}
                <div style={{ fontSize: 11, color: '#9AA3AE', marginTop: 6 }}>
                  {m.auteur?.email ?? 'système'} · {formaterDateHeure(m.created_at)}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.currentTarget.value)}
              placeholder={notePrivee ? 'Note interne…' : 'Réponse au client…'}
              rows={4}
              style={{
                width: '100%',
                borderRadius: 10,
                border: '1px solid #E3E7EC',
                padding: 10,
                font: 'inherit',
                resize: 'vertical',
              }}
            />
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={notePrivee}
                onChange={(e) => setNotePrivee(e.currentTarget.checked)}
              />
              Note privée — n’est jamais envoyée au client et ne date pas la première réponse
            </label>
            <Bouton
              variante="primaire"
              disabled={message.trim() === '' || repondre.isPending}
              onClick={() => {
                repondre.mutate(
                  { message: message.trim(), note_privee: notePrivee },
                  { onSuccess: () => setMessage('') },
                );
              }}
            >
              {notePrivee ? 'Ajouter la note' : 'Répondre'}
            </Bouton>
            {repondre.error ? <MessageErreur erreur={repondre.error} /> : null}
          </div>
        </div>
      ) : null}
    </Carte>
  );
}

function Tuile({
  libelle,
  valeur,
  accent,
  aide,
}: {
  libelle: string;
  valeur: string;
  accent?: string;
  aide?: string;
}): JSX.Element {
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E3E7EC',
        borderRadius: 12,
        padding: '14px 16px',
      }}
    >
      <div style={{ fontSize: 12, color: '#6B7480', textTransform: 'uppercase', letterSpacing: '.04em' }}>
        {libelle}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent ?? '#14181F', marginTop: 4 }}>
        {valeur}
      </div>
      {aide ? <div style={{ fontSize: 11, color: '#9AA3AE', marginTop: 2 }}>{aide}</div> : null}
    </div>
  );
}
